import {
  userCanBookAppointmentsV2,
  bffCrmAppointmentsChangeAppointmentType,
  bffCrmPatientAppointments,
  bffCrmPatientAppointmentCancelations,
  type BffCrmPatientAppointmentsRetrieveResponse,
  type BffCrmPatientAppointmentCancelationsRetrieveResponse,
} from '@fireflyhealth/api'
import { useQueryClient } from '@tanstack/react-query'
import { useMutation, useQuery } from '~/shared/hooks/query'
import { AppointmentTypeMapping, LegacyVisitReason, VisitReason } from './AppointmentService'

export type ApptTypes =
  | 'Medical Issue'
  | 'Health Guide Visit'
  | 'Behavioral Health Visit'
  | 'Annual Wellness '
  | 'Annual Wellness New'
  // Generic fallback title used when the member has no bookable visit types.
  | 'Appointment'

export const VisitTypeMapping: [LegacyVisitReason, ApptTypes][] = [
  ['video', 'Medical Issue'],
  ['videoNew', 'Medical Issue'],
  ['healthGuideConsult', 'Health Guide Visit'],
  ['behavioralHealth', 'Behavioral Health Visit'],
  ['annualWellnessVisitEstablished', 'Annual Wellness '],
  ['annualWellnessVisitNew', 'Annual Wellness New'],
]

// Derive the reverse mapping from AppointmentTypeMapping so we don't duplicate strings
export const reasonToVisitReason: Record<string, VisitReason> = Object.entries(
  AppointmentTypeMapping,
).reduce(
  (acc, [visitReason, display]) => {
    acc[display as string] = visitReason as VisitReason
    return acc
  },
  {} as Record<string, VisitReason>,
)

export const useGetPatientAppointmentsInfo = (patientId: number) =>
  useQuery(
    bffCrmPatientAppointments.retrieveQueryKey({ patient_id: patientId }),
    async () => {
      const response = await bffCrmPatientAppointments.retrieve({
        params: { patient_id: patientId },
      })
      return response.data
    },
    { staleTime: Infinity, enabled: !!patientId },
  )

export const useGetPatientAppointmentCancelationsInfo = (patientId: number) =>
  useQuery(
    bffCrmPatientAppointmentCancelations.retrieveQueryKey({ patient_id: patientId }),
    async () => {
      const response = await bffCrmPatientAppointmentCancelations.retrieve({
        params: { patient_id: patientId },
      })
      return response.data
    },
    { staleTime: Infinity, enabled: !!patientId },
  )

export const useGetCanUserBookAppointmentsV2 = (patientId: number) =>
  useQuery(
    userCanBookAppointmentsV2.retrieveQueryKey({ id: String(patientId) }),
    async () => {
      const response = await userCanBookAppointmentsV2.retrieve({ id: String(patientId) })
      return response.data
    },
    { staleTime: Infinity, enabled: !!patientId },
  )

const changeableAppointmentTypesQueryKey = (appointmentId: number) =>
  bffCrmAppointmentsChangeAppointmentType.retrieveQueryKey({ appointment_id: appointmentId })

export const useChangeableAppointmentTypes = (appointmentId: number) =>
  useQuery(
    changeableAppointmentTypesQueryKey(appointmentId),
    async () => {
      const response = await bffCrmAppointmentsChangeAppointmentType.retrieve({
        params: { appointment_id: appointmentId },
      })
      return response.data
    },
    {},
    { error: 'Failed to load the visit types' },
  )

export const useChangeAppointmentType = (patientId: number) => {
  const client = useQueryClient()
  return useMutation(
    async (variables: { appointmentId: number; reason: string }) => {
      const response = await bffCrmAppointmentsChangeAppointmentType.create({
        data: { appointment_id: variables.appointmentId, reason: variables.reason },
      })
      return response.data
    },
    {
      onSuccess: async () => {
        await client.invalidateQueries({
          queryKey: bffCrmPatientAppointments.retrieveQueryKey({ patient_id: patientId }),
        })
        await client.invalidateQueries({ queryKey: ['useAppointmentLogs', patientId] })
      },
    },
  )
}

export type Appointment = BffCrmPatientAppointmentsRetrieveResponse['appointments'][number]
export type AppointmentCancelation =
  BffCrmPatientAppointmentCancelationsRetrieveResponse['cancelation_events'][number]
